import React from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { FORM_DEFAULTS } from 'talent_matching/filter_form_defaults';
import { useFiltersApplied } from 'talent_matching/hooks/use_people';
import { useFilterCoverage } from 'talent_matching/hooks/use_filter_coverage';
import AdvancedFilter from 'talent_matching/components/filters/advanced_filter';
import {
  AdvancedFilterType,
  FilterType,
  UnmatchedFilterValue,
} from 'talent_matching/types';
import { withLocalePrefix } from 'shared/utils/translation';
import NoResultsOverlay from 'talent_matching/components/shared/no_results_overlay';
import { useTalentMatchingTabContext } from '../contexts/talent_matching_tab_context';

const t = withLocalePrefix('talent_matching.table.no_results');

type Props = {
  unmatchedFilterValues?: UnmatchedFilterValue[];
};

const AppReviewNoResultsOverlay = ({ unmatchedFilterValues }: Props) => {
  const filtersApplied = useFiltersApplied();
  const { reset, getValues } = useFormContext();
  const coverageData = useFilterCoverage();
  const { reviewStages, selectedStageId } = useTalentMatchingTabContext();
  // Name the stage unless it's a lone Application Review stage, which keeps the original copy.
  const currentStage = reviewStages.find(
    (stage) => stage.id === selectedStageId
  );
  const selectedStage =
    currentStage &&
    (reviewStages.length > 1 || currentStage.kind !== 'application_review')
      ? currentStage
      : undefined;

  const handleClearAll = () => {
    reset(FORM_DEFAULTS);
  };

  const zeroCoverageFilterType = (): FilterType | undefined => {
    if (!coverageData) {
      return undefined;
    }

    const appliedFilters = [
      ...((getValues('advancedFilters') as AdvancedFilterType[]) || []),
      ...((getValues('questionFilters') as AdvancedFilterType[]) || []),
    ]
      .map((filter) => new AdvancedFilter(filter))
      .filter((filter) => filter.applied());

    return (
      appliedFilters.find(
        (filter) =>
          filter.type &&
          // "is not defined" on an empty field matches everyone, so it can't be the cause.
          filter.operator?.value !== 'is_not_defined' &&
          coverageData.coverage[filter.type.value] === 0
      )?.type || undefined
    );
  };

  const explainedBody = (): string => {
    const unmatched = unmatchedFilterValues?.[0];
    if (unmatched) {
      return t('unmatched_value_body', {
        label: unmatched.label,
        value: unmatched.values.join(', '),
      });
    }

    const emptyFilterType = zeroCoverageFilterType();
    if (emptyFilterType) {
      return t('no_coverage_body', { label: emptyFilterType.label });
    }

    return t('body');
  };

  return (
    <NoResultsOverlay
      hasAnyFilters={filtersApplied}
      onClearAll={handleClearAll}
      filteredHeader={t('header')}
      filteredBody={explainedBody()}
      clearButtonLabel={t('button')}
      emptyHeader={
        selectedStage
          ? t('empty_in_stage', { stageName: selectedStage.name })
          : t('empty')
      }
      emptyBody={selectedStage ? t('empty_in_stage_body') : undefined}
    />
  );
};

export default AppReviewNoResultsOverlay;
